import React, { useState, useMemo, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { useDataGridLocaleText } from "../i18n/useDataGridLocale";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  TextField,
  InputBase,
  Badge,
  Tooltip,
  IconButton,
  CircularProgress,
  Skeleton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { Search, Tune } from "@mui/icons-material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";

const GenericTable = ({
  rows = [],
  columns = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading,
  onExportPDFTitle,
  exportFileName = "dados_exportados",
  paginationMode = "server", // "server" | "client"
  /** Quando "server", o pai deve buscar dados já ordenados (ex.: GET /products?sort_by=…). */
  sortingMode,
  sortModel,
  onSortModelChange,

  // novos/ajustados
  rightActions,
  onColumnWidthChange,
  advancedFilters,
  /** Abre modal de filtros externo (ex.: Clientes). Ícone Tune dentro do input, como no header. */
  onOpenAdvancedFilters,
  advancedFiltersActiveCount = 0,

  // [NEW] exclusão em massa
  onDeleteSelected, // (ids) => Promise|void
  deleteSelectedLabel,
  deleteConfirmTitle,
  deleteConfirmMessage,

  // [NEW] expor seleção p/ parent (opcional)
  onSelectionChange,
  onRowClick, // (row) => void 
  isRowSelectable, // (params) => boolean (DataGrid)
  /** Ações adicionais quando há seleção (ex.: unificar). */
  bulkActions, // ReactNode | ({ selectedRows, selectedCount }) => ReactNode

  /** Quando false, oculta checkboxes e export em massa (ex.: listagens sem exclusão em lote). */
  checkboxSelection = true,
  /** Quando false, oculta botões PDF/CSV da barra de seleção. */
  showExport = true,
  /** Altura fixa do grid para virtualização com scroll (ex.: 520). */
  tableMaxHeight,
  /** Altura das linhas do DataGrid (número) ou omitir para o padrão. */
  rowHeight,
  /** Ex.: () => "auto" para células com conteúdo multilinha. */
  getRowHeight,
  /** sx adicional mesclado no DataGrid. */
  dataGridSx,
  /** Busca controlada pelo parent (filtra a cada tecla). Se definido, use com `onSearchChange`. */
  searchValue,
  onSearchChange,
  /** Debounce da busca server-side. Suba em tabelas grandes (ex.: clientes). */
  searchDebounceMs = 800,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchInput, setSearchInput] = useState("");
  const controlledSearch = searchValue !== undefined && typeof onSearchChange === "function";

  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  /**
   * Server-side tables: debounce estável (callback via ref para não resetar o timer a cada render).
   *
   * 400ms era curto demais para listagens grandes: cancelar o request HTTP não mata a query
   * no banco, então cada pausa da digitação deixava mais uma busca rodando no servidor.
   */
  const debouncedOnSearch = useMemo(() => {
    if (controlledSearch) return null;
    return debounce((value) => {
      const normalized = value == null ? "" : String(value);
      onSearchRef.current?.(normalized);
    }, searchDebounceMs);
  }, [controlledSearch, searchDebounceMs]);

  useEffect(() => {
    return () => debouncedOnSearch?.cancel();
  }, [debouncedOnSearch]);
  const dataGridLocaleText = useDataGridLocaleText();
  const { t } = useTranslation("common");
  const { t: ts } = useTranslation("shell");
  const tSearch = t("genericTable.search");
  const tAdvancedFilters = t("genericTable.advancedFilters");
  const tExportPdf = t("genericTable.exportPdf");
  const tExportCsv = t("genericTable.exportCsv");
  const tSelectedCount = (count) => t("genericTable.selectedCount", { count });
  const tSelectedShort = (count) => t("genericTable.selectedShort", { count });
  const tDeleteSelected = deleteSelectedLabel || t("genericTable.deleteSelected");
  const tDeleteConfirmTitle = deleteConfirmTitle || t("genericTable.deleteSelectedTitle");
  const tDeleteConfirmMessage =
    deleteConfirmMessage ||
    ((count) => t("genericTable.deleteSelectedConfirm", { count }));
  const tDeleting = t("genericTable.deleting");
  const tCancel = t("cancel");
  const tDelete = t("delete");
  const effectiveExportTitle = onExportPDFTitle || t("genericTable.exportDataTitle");
  const bulkActionsNode =
    typeof bulkActions === "function"
      ? bulkActions({
          selectedIds: selectedRows,
          selectedRows: rows.filter((row) => selectedRows.includes(row.id)),
          selectedCount: selectedRows.length,
        })
      : bulkActions;

  // [NEW] estado de confirmação/execução da exclusão em massa
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text(effectiveExportTitle, 20, 10);
    const selectedData = rows.filter((row) => selectedRows.includes(row.id));
    const headers = columns.map((col) => col.headerName || col.field);
    const data = selectedData.map((row) => columns.map((col) => row[col.field]));
    doc.autoTable({ head: [headers], body: data });
    doc.save(`${exportFileName}.pdf`);
  };

  const selectedDataForCSV = rows.filter((row) => selectedRows.includes(row.id));
  const csvData = selectedDataForCSV.map((row) => {
    const csvRow = {};
    columns.forEach((col) => {
      csvRow[col.headerName || col.field] = row[col.field];
    });
    return csvRow;
  });

  const renderFilterField = (f) => {
    const common = {
      fullWidth: true,
      size: "small",
    };

    switch (f.type) {
      case "select":
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{f.label}</InputLabel>
            <Select
              label={f.label}
              value={f.value ?? ""}
              onChange={(e) => f.onChange?.(e.target.value)}
            >
              {(f.options || []).map((opt) => (
                <MenuItem key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "number":
        return (
          <TextField
            {...common}
            label={f.label}
            type="number"
            value={f.value ?? ""}
            onChange={(e) => f.onChange?.(e.target.value)}
            inputProps={f.inputProps}
          />
        );
      default:
        return (
          <TextField
            {...common}
            label={f.label}
            value={f.value ?? ""}
            onChange={(e) => f.onChange?.(e.target.value)}
          />
        );
    }
  };

  const handleOpenBulkDelete = () => {
    if (selectedRows.length === 0 || !onDeleteSelected) return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (!onDeleteSelected || selectedRows.length === 0) {
      setConfirmDeleteOpen(false);
      return;
    }
    try {
      setBulkDeleting(true);
      // deixa a política para o parent (ex: Promise.allSettled / endpoint em lote)
      await Promise.resolve(onDeleteSelected(selectedRows));
      // sucesso — limpa seleção
      setSelectedRows([]);
      onSelectionChange?.([]);
    } finally {
      setBulkDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  // evita disparar edição quando o alvo é um botão/checkbox/link etc.
  const isInteractiveTarget = (event) => {
    const el = event.target;
    return !!el.closest?.(
      'button, a, input, textarea, select, [role="button"], .MuiCheckbox-root, .MuiSwitch-root, .MuiIconButton-root'
    );
  };

  const hasAdvancedSearch = Boolean(advancedFilters || onOpenAdvancedFilters);
  const searchValueResolved = controlledSearch ? searchValue : searchInput;

  const handleSearchInputChange = (v) => {
    const normalized = v == null ? "" : String(v);
    if (controlledSearch) {
      onSearchChange(normalized);
    } else {
      setSearchInput(normalized);
      debouncedOnSearch?.(normalized);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (controlledSearch) return;
    debouncedOnSearch?.cancel();
    const v = e.currentTarget?.value == null ? "" : String(e.currentTarget.value);
    onSearch?.(v);
  };

  const openAdvancedFilters = () => {
    if (typeof onOpenAdvancedFilters === "function") {
      onOpenAdvancedFilters();
      return;
    }
    setFilterModalOpen(true);
  };

  const renderHeaderStyleSearch = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        px: 1.5,
        py: 0.5,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Search sx={{ mr: 1, color: theme.palette.text.secondary, fontSize: 22 }} />
      <InputBase
        placeholder={hasAdvancedSearch ? t("searchPlaceholder") : tSearch}
        fullWidth
        value={searchValueResolved ?? ""}
        onChange={(e) => handleSearchInputChange(e.target.value)}
        onKeyDown={handleSearchKeyDown}
        sx={{ fontSize: "16px", color: theme.palette.text.primary }}
      />
      <Tooltip title={tAdvancedFilters}>
        <IconButton
          onClick={openAdvancedFilters}
          size="small"
          aria-label={ts("search.advancedSearchAria")}
        >
          <Badge
            color="secondary"
            badgeContent={advancedFiltersActiveCount > 0 ? advancedFiltersActiveCount : null}
            overlap="circular"
          >
            <Tune fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Paper>
      <Box px={2} py={2}>
        {/* Topo: busca + filtros */}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          alignItems={isMobile ? "stretch" : "center"}
          gap={1}
          mb={1.5}
        >
          {hasAdvancedSearch ? (
            renderHeaderStyleSearch()
          ) : (
            <TextField
              fullWidth
              label={tSearch}
              variant="outlined"
              value={searchValueResolved ?? ""}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          )}
        </Box>

        {/* Slot para ações extras */}
        {rightActions && (
          <Box mb={1.5} display="flex" justifyContent="flex-end">
            {rightActions}
          </Box>
        )}

        {/* Barra contextual de ações em massa */}
        {checkboxSelection && selectedRows.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: { xs: 1.25, md: 1 },
            }}
          >
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={tSelectedShort(selectedRows.length)}
              title={tSelectedCount(selectedRows.length)}
              sx={{ alignSelf: { xs: "flex-start", md: "center" }, fontWeight: 600 }}
            />

            {bulkActionsNode ? (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", md: "block" }, mx: 0.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.75,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {bulkActionsNode}
                </Box>
              </>
            ) : null}

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.75,
                ml: { md: bulkActionsNode ? 0 : "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              {showExport ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={exportPDF}
                    startIcon={<PictureAsPdfOutlinedIcon fontSize="small" />}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                  >
                    {tExportPdf}
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<TableChartOutlinedIcon fontSize="small" />}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                  >
                    <CSVLink
                      data={csvData}
                      filename={`${exportFileName}.csv`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      {tExportCsv}
                    </CSVLink>
                  </Button>
                </>
              ) : null}

              {onDeleteSelected ? (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineRoundedIcon fontSize="small" />}
                  onClick={handleOpenBulkDelete}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                >
                  {tDeleteSelected}
                </Button>
              ) : null}
            </Box>
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(columns || []).slice(0, 5).map((col, i) => (
                    <TableCell key={col.field || i}>
                      <Skeleton variant="text" width={col.width ? Math.min(col.width, 120) : 80} height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                  <TableRow key={row}>
                    {(columns || []).slice(0, 5).map((col, i) => (
                      <TableCell key={col.field || i}>
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight={!tableMaxHeight}
            localeText={dataGridLocaleText}
            pagination
            paginationMode={paginationMode}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick
            isRowSelectable={isRowSelectable}
            {...(rowHeight != null ? { rowHeight } : {})}
            {...(typeof getRowHeight === "function" ? { getRowHeight } : {})}
            {...(checkboxSelection
              ? {
                  rowSelectionModel: selectedRows,
                  onRowSelectionModelChange: (newSelection) => {
                    setSelectedRows(newSelection);
                    onSelectionChange?.(newSelection);
                  },
                }
              : {})}
            paginationModel={{ page, pageSize }}
            rowCount={paginationMode === "server" ? rowCount : undefined}
            onPaginationModelChange={(model) => {
              onPageChange?.(model.page);
              onPageSizeChange?.(model.pageSize);
            }}
            {...(sortingMode ? { sortingMode } : {})}
            {...(typeof onSortModelChange === "function"
              ? {
                  sortModel: sortModel ?? [],
                  onSortModelChange,
                }
              : {})}
            // Use uma das duas conforme versão do DataGrid:
            onColumnWidthChange={onColumnWidthChange} // v6 (commit)
            onColumnResize={onColumnWidthChange}      // v5 (durante o resize)
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            onCellClick={(params, event) => {
              if (!onRowClick) return;
              // ignora: cliques em botões/links/inputs/checkbox...
              if (isInteractiveTarget(event)) return;
              // ignora: coluna de ações e coluna de checkbox interno
              if (params.field === "actions" || params.field === "__check__") return;
              // dispara edição com a row inteira
              onRowClick(params.row);
            }}
            // opcional: cursor "pointer" para indicar que dá pra clicar
            sx={{
              ...(tableMaxHeight
                ? { height: tableMaxHeight, maxHeight: tableMaxHeight }
                : {}),
              "& .MuiDataGrid-row:hover": { cursor: onRowClick ? "pointer" : "default" },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                ...(typeof getRowHeight === "function" ? { py: 1.25 } : {}),
              },
              ...dataGridSx,
            }}
          />
        )}
      </Box>

      {/* Filtros Avançados */}
      {advancedFilters && (
        <Dialog
          open={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{tAdvancedFilters}</DialogTitle>
          <DialogContent dividers>
            {Array.isArray(advancedFilters.fields) && advancedFilters.fields.length > 0 ? (
              <Grid container spacing={2}>
                {advancedFilters.fields.map((f) => (
                  <Grid key={f.key} item xs={12} sm={6}>
                    {renderFilterField(f)}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary">
                Nenhum filtro configurado.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Box>
              <Button
                onClick={() => {
                  advancedFilters.onClear?.();
                  setFilterModalOpen(false);
                }}
              >
                Limpar
              </Button>
            </Box>
            <Box>
              <Button onClick={() => setFilterModalOpen(false)}>Fechar</Button>
              <Button
                variant="contained"
                onClick={() => {
                  advancedFilters.onApply?.();
                  setFilterModalOpen(false);
                }}
                sx={{ ml: 1 }}
              >
                Aplicar
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* [NEW] Confirmação de exclusão em massa */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={bulkDeleting ? undefined : () => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>{tDeleteConfirmTitle}</DialogTitle>
        <DialogContent>
          <Typography>
            {bulkDeleting
              ? tDeleting
              : tDeleteConfirmMessage(selectedRows.length)}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!bulkDeleting && (
            <>
              <Button onClick={() => setConfirmDeleteOpen(false)}>{tCancel}</Button>
              <Button color="error" onClick={handleConfirmBulkDelete}>
                {tDelete}
              </Button>
            </>
          )}
          {bulkDeleting && <CircularProgress size={20} sx={{ m: 2 }} />}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GenericTable;
